import { useCreateSelector } from '@insights-gaming/redux-utils';
import { useBooleanState } from '@insights-gaming/utils';
import update from 'immutability-helper';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProductTourSelectors } from '../selectors';

export function makeProductTourHooks<R, T extends string>(
  selectors: ProductTourSelectors<R, T>,
) {
  const {
    getViewedProductTours,
    makeShouldShowTourId,
  } = selectors;

  function useProductTourState(tourId: T): readonly [boolean, VoidFunction, VoidFunction] {
    const [isProductTourOpen, openProductTour, closeProductTour] = useBooleanState(false);
    const shouldShow = useCreateSelector(makeShouldShowTourId, { tourId });

    return [
      isProductTourOpen,
      useCallback(() => {
        if (shouldShow) {
          openProductTour();
        }
      }, [openProductTour, shouldShow]),
      closeProductTour,
    ] as const;
  }

  function useProductTourOrder(productTourIds: T[]) {
    const idsRef = useRef(productTourIds);
    const viewedIds = useSelector(getViewedProductTours);
    return useMemo(() => idsRef.current.find(tourId => !viewedIds[tourId]), [viewedIds]);
  }

  function useMultiStepProductTour(length: number): readonly [(HTMLElement | null)[], ((el: HTMLElement | null) => void)[]] {
    const [anchors, setAnchors] = useState<(HTMLElement | null)[]>(Array.from({ length }));
    const refHandlers = useMemo(() => {
      return Array.from({ length })
        .map((_, i) =>
          (el: HTMLElement | null) => {
            setAnchors(p => update(p, { [i]: { $set: el } }));
          },
        );
    }, [length]);
    return [anchors, refHandlers] as const;
  }

  function useSingleStepProductTour(): readonly [HTMLElement | null, (el: HTMLElement | null) => void] {
    const [[anchor], [setAnchor]] = useMultiStepProductTour(1);
    return [anchor, setAnchor] as const;
  }

  return {
    useMultiStepProductTour,
    useProductTourOrder,
    useProductTourState,
    useSingleStepProductTour,
  };
}
